import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Link
} from '@mui/material';
import {
  Add as AddIcon, ContentCopy as CopyIcon, DeleteOutline as DeleteIcon, Email as EmailIcon, Language as DomainIcon, PauseCircle as SuspendIcon, PlayCircle as ActivateIcon,
  SwitchAccount as ActAsIcon, ExpandMore as ExpandIcon
} from '@mui/icons-material';
import axios from 'axios';

import { API, getAuthHeader as authHeader, money } from './customerApp/shared';
import { startActAs } from '../utils/actAs';

const BASE = `${API}/api/partners-admin`;
const PLATFORM = `${API}/api/platform`;

const EMPTY = { name: '', slug: '', firstName: '', lastName: '', email: '', phone: '', username: '' };
const slugify = (s) => {
  const full = String(s || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (full.length <= 32) return full;
  const cut = full.slice(0, 32);
  const atWord = cut.lastIndexOf('-');
  return (atWord > 0 ? cut.slice(0, atWord) : cut).replace(/-+$/g, '');
};

const copy = (text) => navigator.clipboard?.writeText(text).catch(() => {});

/** Owner Console → Partners → Partner companies: create and manage partner motor clubs (White Knight owners only). */
export default function PartnerCompanies() {
  const [rows, setRows] = useState([]);
  const [baseDomain, setBaseDomain] = useState('wkmc.services');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState({ checking: false, available: null });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // { partner, urls, setupUrl, emailSent }

  const [domainsFor, setDomainsFor] = useState(null); // row being edited
  const [domainForm, setDomainForm] = useState({ customPortal: '', customCustomer: '' });
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState('');

  const [counts, setCounts] = useState({}); // org id → counts from /api/platform/organizations
  const [billing, setBilling] = useState(null); // null = not loaded, '' = loading
  const [billingOpen, setBillingOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(BASE, { headers: authHeader() });
      setRows(data.partners || []);
      if (data.baseDomain) setBaseDomain(data.baseDomain);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load partner companies');
    } finally {
      setLoading(false);
    }
    // People, customers and jobs per company (separate call: the list must still show if it fails)
    try {
      const { data } = await axios.get(`${PLATFORM}/organizations`, { headers: authHeader() });
      setCounts(Object.fromEntries((data.organizations || []).map((o) => [o.id, o.counts])));
    } catch { /* counts stay blank */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadBilling = useCallback(async () => {
    setBilling('');
    try {
      const { data } = await axios.get(`${PLATFORM}/billing`, { params: { days: 30 }, headers: authHeader() });
      setBilling(data);
    } catch (err) {
      setBilling(null);
      setError(err.response?.data?.message || 'Could not load billing');
    }
  }, []);
  useEffect(() => { if (billingOpen && billing === null) loadBilling(); }, [billingOpen, billing, loadBilling]);

  // Work inside a partner company as yourself for 30 minutes (banner at the top, "Stop" to return)
  const actAs = async (row) => {
    setBusyId(row.id);
    setError('');
    try {
      const { data } = await axios.post(`${PLATFORM}/organizations/${row.id}/act-as`, {}, { headers: authHeader() });
      startActAs(data);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start acting as that company');
      setBusyId(null);
    }
  };

  // Slug follows the company name until the owner edits it; availability is checked live
  useEffect(() => {
    if (!open) return undefined;
    const slug = form.slug;
    if (!slug) { setSlugState({ checking: false, available: null }); return undefined; }
    setSlugState({ checking: true, available: null });
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${BASE}/slug-available`, { params: { slug }, headers: authHeader() });
        setSlugState({ checking: false, available: data.available });
      } catch {
        setSlugState({ checking: false, available: null });
      }
    }, 350);
    return () => clearTimeout(id);
  }, [form.slug, open]);

  const update = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'name' && !slugTouched) next.slug = slugify(value);
      if (key === 'slug') setSlugTouched(true);
      if ((key === 'firstName' || key === 'lastName') && !f.username) {
        next.username = slugify(`${key === 'firstName' ? value : f.firstName}.${key === 'lastName' ? value : f.lastName}`).replace(/-/g, '.');
      }
      return next;
    });
  };

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(BASE, {
        name: form.name, slug: form.slug,
        owner: { firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone, username: form.username }
      }, { headers: authHeader() });
      setCreated(data);
      setOpen(false);
      setForm(EMPTY);
      setSlugTouched(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create the partner company');
    } finally {
      setSaving(false);
    }
  };

  const patch = async (row, body, label) => {
    setBusyId(row.id);
    setNotice('');
    try {
      await axios.patch(`${BASE}/${row.id}`, body, { headers: authHeader() });
      setNotice(`${row.name}: ${label}`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the partner company');
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (row) => {
    setBusyId(row.id);
    setNotice('');
    try {
      const { data } = await axios.post(`${BASE}/${row.id}/resend-welcome`, {}, { headers: authHeader() });
      setCreated({ partner: row, setupUrl: data.setupUrl, emailSent: data.emailSent, urls: null, resent: true, email: data.email });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend the welcome email');
    } finally {
      setBusyId(null);
    }
  };

  const [deleteFor, setDeleteFor] = useState(null);
  const remove = async () => {
    const row = deleteFor;
    setDeleteFor(null);
    setBusyId(row.id);
    setNotice('');
    try {
      await axios.delete(`${BASE}/${row.id}`, { headers: authHeader() });
      setNotice(`${row.name} was deleted`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not delete the partner company');
    } finally {
      setBusyId(null);
    }
  };

  const saveDomains = async () => {
    const row = domainsFor;
    setDomainsFor(null);
    await patch(row, { hosts: { customPortal: domainForm.customPortal, customCustomer: domainForm.customCustomer } }, 'custom domains saved (they go live once DNS points here)');
  };

  const statusChip = (row) => {
    if (row.status === 'suspended') return <Chip size="small" color="error" label="Suspended" />;
    if (row.owner?.setupPending) return <Chip size="small" color="warning" label="Awaiting password" />;
    if (!row.setup?.completedAt) return <Chip size="small" color="info" label={`Setup ${row.setup?.completedSteps?.length || 0}/9`} />;
    return <Chip size="small" color="success" label="Active" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Partner companies</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setOpen(true); setError(''); }}>New partner company</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each partner is a separate motor club with its own portal, customer website, provider application form, customers, providers and jobs.
        Creating one emails its owner a one-time link to set a password and run the setup wizard.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Portal</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>People</TableCell>
                <TableCell>Customers</TableCell>
                <TableCell>Jobs</TableCell>
                <TableCell>Payments</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9}><Typography color="text.secondary">No partner companies yet.</Typography></TableCell></TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.slug}</Typography>
                  </TableCell>
                  <TableCell>
                    <Link href={`https://${row.hosts?.customPortal || row.hosts?.portal}`} target="_blank" rel="noopener" variant="body2">{row.hosts?.customPortal || row.hosts?.portal}</Link><br />
                    <Link href={`https://${row.hosts?.customCustomer || row.hosts?.customer}`} target="_blank" rel="noopener" variant="caption">{row.hosts?.customCustomer || row.hosts?.customer}</Link>
                  </TableCell>
                  <TableCell>
                    {row.owner ? (<><Typography variant="body2">{row.owner.name}</Typography><Typography variant="caption" color="text.secondary">{row.owner.email}</Typography></>) : '—'}
                  </TableCell>
                  <TableCell>{statusChip(row)}</TableCell>
                  <TableCell>
                    {counts[row.id] ? (<><Typography variant="body2">{counts[row.id].staff} staff</Typography><Typography variant="caption" color="text.secondary">{counts[row.id].providers} providers</Typography></>) : '—'}
                  </TableCell>
                  <TableCell>{counts[row.id] ? counts[row.id].customers : '—'}</TableCell>
                  <TableCell>
                    {counts[row.id] ? (<><Typography variant="body2">{counts[row.id].jobs.open} open · {counts[row.id].jobs.total} total</Typography><Typography variant="caption" color="text.secondary">{counts[row.id].jobs.last30} in 30 days{counts[row.id].applications.pending ? ` · ${counts[row.id].applications.pending} applications waiting` : ''}</Typography></>) : '—'}
                  </TableCell>
                  <TableCell>
                    {row.demoMode
                      ? <Chip size="small" variant="outlined" label={row.stripe?.connected ? (row.stripe.chargesEnabled ? 'Demo · Stripe verified' : 'Demo · Stripe onboarding') : 'Demo mode'} />
                      : <Chip size="small" color="success" variant="outlined" label={row.stripe?.chargesEnabled ? 'Stripe live' : 'Stripe pending'} />}
                    <Tooltip title="White Knight's share of each card payment this company takes (0 to 50%)">
                      <TextField
                        size="small" type="number" label="Fee %" sx={{ width: 84, mt: 0.75, display: 'block' }}
                        defaultValue={row.stripe?.platformFeePct ?? 0} key={`${row.id}-${row.stripe?.platformFeePct}`}
                        inputProps={{ min: 0, max: 50, step: 0.5 }} disabled={busyId === row.id}
                        onBlur={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && v !== (row.stripe?.platformFeePct ?? 0)) patch(row, { platformFeePct: v }, `platform fee set to ${v}%`); }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Act as this company for 30 minutes: see and change its data as yourself">
                      <span><IconButton size="small" color="primary" disabled={busyId === row.id} onClick={() => actAs(row)}><ActAsIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Resend the setup link / welcome email">
                      <span><IconButton size="small" disabled={busyId === row.id} onClick={() => resend(row)}><EmailIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Custom domains">
                      <span><IconButton size="small" disabled={busyId === row.id} onClick={() => { setDomainsFor(row); setDomainForm({ customPortal: row.hosts?.customPortal || '', customCustomer: row.hosts?.customCustomer || '' }); }}><DomainIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    {row.status === 'suspended' ? (
                      <Tooltip title="Reactivate"><span><IconButton size="small" color="success" disabled={busyId === row.id} onClick={() => patch(row, { status: 'active' }, 'reactivated')}><ActivateIcon fontSize="small" /></IconButton></span></Tooltip>
                    ) : (
                      <Tooltip title="Suspend (their portal and site stop answering)"><span><IconButton size="small" color="warning" disabled={busyId === row.id} onClick={() => patch(row, { status: 'suspended' }, 'suspended')}><SuspendIcon fontSize="small" /></IconButton></span></Tooltip>
                    )}
                    <Tooltip title={row.demoMode ? 'Demo mode on: payments are simulated' : 'Demo mode off'}>
                      <FormControlLabel sx={{ ml: 0.5 }} control={<Switch size="small" checked={!!row.demoMode} disabled={busyId === row.id} onChange={(e) => patch(row, { demoMode: e.target.checked }, e.target.checked ? 'demo mode on' : 'demo mode off')} />} label={<Typography variant="caption">Demo</Typography>} />
                    </Tooltip>
                    <Tooltip title="Delete (only while the company has no jobs or customers)">
                      <span><IconButton size="small" color="error" disabled={busyId === row.id} onClick={() => setDeleteFor(row)}><DeleteIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Platform billing: card payments per company and White Knight's share */}
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, cursor: 'pointer' }} onClick={() => setBillingOpen((o) => !o)}>
          <Typography variant="subtitle1">Platform billing (last 30 days)</Typography>
          <IconButton size="small" sx={{ transform: billingOpen ? 'rotate(180deg)' : 'none' }}><ExpandIcon /></IconButton>
        </Box>
        {billingOpen && (
          <Box sx={{ px: 2, pb: 2 }}>
            {billing === '' || billing === null ? <CircularProgress size={20} /> : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Card payments</TableCell>
                      <TableCell align="right">Card volume</TableCell>
                      <TableCell align="right">Cash</TableCell>
                      <TableCell align="right">Platform share</TableCell>
                      <TableCell align="right">All-time share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billing.organizations.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>{o.name}{o.kind === 'platform' ? ' (White Knight)' : ''}{o.demoMode ? <Chip size="small" variant="outlined" label="Demo" sx={{ ml: 1 }} /> : null}</TableCell>
                        <TableCell align="right">{o.kind === 'platform' ? '—' : `${o.platformFeePct}%`}</TableCell>
                        <TableCell align="right">{o.period.count}</TableCell>
                        <TableCell align="right">{money(o.period.grossCents)}</TableCell>
                        <TableCell align="right">{money(o.period.cashCents)}</TableCell>
                        <TableCell align="right">{o.kind === 'platform' ? '—' : money(o.period.feeCents)}</TableCell>
                        <TableCell align="right">{o.kind === 'platform' ? '—' : money(o.allTime.feeCents)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}><b>Total platform share</b></TableCell>
                      <TableCell align="right"><b>{money(billing.totals.period.feeCents)}</b></TableCell>
                      <TableCell align="right"><b>{money(billing.totals.allTime.feeCents)}</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{billing.note}</Typography>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Create dialog */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New partner company</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          <TextField label="Company name" value={form.name} onChange={update('name')} autoFocus required />
          <TextField
            label="Web address"
            value={form.slug}
            onChange={update('slug')}
            required
            helperText={
              slugState.checking ? 'Checking…'
                : slugState.available === false ? 'Not available'
                  : `${form.slug || 'name'}.${baseDomain} (portal) and ${form.slug || 'name'}-roadside.${baseDomain} (customer site). Cannot be changed later.`
            }
            error={slugState.available === false}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField label="Owner first name" value={form.firstName} onChange={update('firstName')} required />
            <TextField label="Owner last name" value={form.lastName} onChange={update('lastName')} required />
          </Box>
          <TextField label="Owner email" type="email" value={form.email} onChange={update('email')} required helperText="The welcome email and setup link go here" />
          <TextField label="Owner phone" value={form.phone} onChange={update('phone')} required helperText="10 digits; also used as the company's support number until they change it" />
          <TextField label="Owner username" value={form.username} onChange={update('username')} required helperText="How they sign in to their portal and the driver app" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving || !form.name || !form.slug || slugState.available === false || !form.firstName || !form.lastName || !form.email || !form.phone || !form.username}>
            {saving ? 'Creating…' : 'Create and send welcome email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created / resent dialog: the setup link is shown here in case the email does not arrive */}
      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>{created?.resent ? 'Setup link renewed' : 'Partner company created'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
          <Alert severity={created?.emailSent ? 'success' : 'warning'}>
            {created?.emailSent
              ? `The welcome email was sent to ${created?.email || created?.partner?.owner?.email}.`
              : 'The welcome email could not be sent. Give the owner the setup link below another way.'}
          </Alert>
          {created?.urls && (
            <Box>
              <Typography variant="subtitle2">Their addresses</Typography>
              {Object.entries(created.urls).map(([k, v]) => (
                <Typography key={k} variant="body2"><Link href={v} target="_blank" rel="noopener">{v}</Link></Typography>
              ))}
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2">One-time setup link (expires in 7 days)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField size="small" fullWidth value={created?.setupUrl || ''} InputProps={{ readOnly: true }} />
              <IconButton onClick={() => copy(created?.setupUrl)}><CopyIcon /></IconButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreated(null)}>Done</Button></DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteFor} onClose={() => setDeleteFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleteFor?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the company, its owner account, its copied settings and its web addresses. It cannot be undone.
            Companies that already have jobs or customers cannot be deleted; suspend those instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFor(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Custom domains dialog */}
      <Dialog open={!!domainsFor} onClose={() => setDomainsFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Custom domains for {domainsFor?.name}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">
            The partner points a CNAME at <b>{domainsFor?.hosts?.portal}</b> (portal) and <b>{domainsFor?.hosts?.customer}</b> (customer site). A certificate is issued automatically the first time the domain is visited after DNS resolves.
          </Typography>
          <TextField label="Portal domain" placeholder="portal.theirclub.com" value={domainForm.customPortal} onChange={(e) => setDomainForm((d) => ({ ...d, customPortal: e.target.value }))} />
          <TextField label="Customer site domain" placeholder="www.theirclub.com" value={domainForm.customCustomer} onChange={(e) => setDomainForm((d) => ({ ...d, customCustomer: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainsFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveDomains}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
